import { LinearProgress } from '@mui/joy';

import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Text } from '@/design-system';
import { useCurrentTier, useOrgRequiresPayment } from '@/hooks/useCurrentTier';
import {
  useCurrentOrganization,
  useSelectedTenant,
  useSelectedWorkspace,
} from '@/hooks/useSwr';
import { appOrganizationPath, backendAuthType } from '@/utils/constants';

import { WorkspaceSettingsHeader } from './components/WorkspaceSettingsHeader';

export function SettingsLayoutWrapper() {
  const { data: workspace } = useSelectedTenant();
  const { data: org } = useCurrentOrganization();

  // There are small amounts of renders when the loaded org information is different
  // from the workspace organization id. Workspace is already loaded while org
  // is not stored in memory and loaded again when you switch to a different organization
  if (backendAuthType !== 'none' && org?.id !== workspace?.organization_id)
    return (
      <div>
        <LinearProgress />
      </div>
    );
  return <SettingsLayout />;
}

const PAGE_TO_TITLE: Record<string, string> = {
  workspaces: 'Workspaces',
  members: 'Organization members and roles',
  usage: 'Usage',
  payments: 'Billing and usage',
};

export function SettingsLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const org = useCurrentOrganization();
  const { isNoPlan } = useCurrentTier();

  const selectedWorkspace = useSelectedWorkspace();

  const currentURL = location.pathname;
  const page = Object.keys(PAGE_TO_TITLE).find((key) =>
    currentURL.includes(key)
  );

  const { requiresPayment } = useOrgRequiresPayment();

  useEffect(() => {
    if (
      !org.isLoading &&
      isNoPlan &&
      selectedWorkspace.data?.id &&
      !currentURL.includes('payments') &&
      requiresPayment
    ) {
      navigate(
        `/${appOrganizationPath}/${selectedWorkspace.data.id}/settings/payments`
      );
    }

    // redirect to workspace default if it's not personal org
    // suffix for default settings page is different for personal org and non-personal org
    if (
      currentURL.endsWith('/settings') &&
      !org.data?.is_personal &&
      selectedWorkspace.data?.id
    ) {
      navigate(
        `/${appOrganizationPath}/${selectedWorkspace.data.id}/settings/workspaces`
      );
    }
    if (
      currentURL.endsWith('/settings') &&
      org.data?.is_personal &&
      selectedWorkspace.data?.id
    ) {
      navigate(
        `/${appOrganizationPath}/${selectedWorkspace.data.id}/settings/apikeys`
      );
    }
  }, [
    currentURL,
    isNoPlan,
    selectedWorkspace.data?.id,
    org.isLoading,
    navigate,
    requiresPayment,
  ]);

  if (selectedWorkspace.isLoading || org.isLoading) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col px-8 pb-6 pt-3">
      {selectedWorkspace.data?.is_personal ? (
        <div className="py-2"></div>
      ) : (
        <div className="flex items-center justify-between">
          {page === 'workspaces' ? (
            <WorkspaceSettingsHeader />
          ) : (
            <Text variant="h1" className="py-4 text-3xl">
              {PAGE_TO_TITLE[page || '']}
            </Text>
          )}
        </div>
      )}
      <Outlet />
    </div>
  );
}
